import React, { useState } from 'react';
import { Globe, ChevronRight, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PrivacyPolicyProps {
  defaultLang?: 'en' | 'fr';
}

interface Section {
  id: string;
  title: string;
  text: string;
}

interface LanguageContent {
  title: string;
  version: string;
  langBtn: string;
  footerNote: string;
  backBtn: string;
  sections: Section[];
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ defaultLang = 'en' }) => {
  const [lang, setLang] = useState<'en' | 'fr'>(defaultLang);

  const content: Record<'en' | 'fr', LanguageContent> = {
    en: {
      title: "PRIVACY POLICY",
      version: "VERSION: 2025-01-15 REV. 1.0",
      langBtn: "FR",
      backBtn: "Back to Form",
      footerNote: "By using AcquizGroup, you acknowledge that you have read and understood this Privacy Policy.",
      sections: [
        {
          id: "1",
          title: "1. Introduction",
          text: "AcquizGroup ('we', 'our', or 'us') is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. By accessing or using AcquizGroup, you consent to the practices described in this policy."
        },
        {
          id: "2",
          title: "2. Information We Collect",
          text: "We collect personal information that you voluntarily provide when registering on our platform, including but not limited to: name, email address, phone number, business address, company information, financial data, professional credentials, and identity verification documents. We also automatically collect certain technical information such as IP addresses, browser type, device information, and usage data through cookies and similar technologies."
        },
        {
          id: "3",
          title: "3. How We Use Your Information",
          text: "We use your personal information to: provide and maintain our platform services; process transactions and facilitate business matchmaking; verify your identity and credentials; communicate with you regarding your account, updates, and promotional materials; improve our services and develop new features; comply with legal obligations and enforce our terms of service; and conduct background checks where required for certain user roles (Lenders, Attorneys, CPAs, Consultants)."
        },
        {
          id: "4",
          title: "4. Information Sharing and Disclosure",
          text: "We may share your information with: other platform users as necessary to facilitate business transactions (subject to NDA agreements); third-party service providers who assist in operating our platform; background check and verification services; payment processors for transaction handling; legal authorities when required by law or to protect our rights. We do not sell your personal information to third parties for marketing purposes."
        },
        {
          id: "5",
          title: "5. Data Security",
          text: "We implement industry-standard security measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee its absolute security. You are responsible for maintaining the confidentiality of your account credentials."
        },
        {
          id: "6",
          title: "6. Confidential Information Memorandum (CIM)",
          text: "Financial documents and business information submitted as part of the Confidential Information Memorandum are treated with the highest level of confidentiality. Access to CIM data is restricted to verified users who have signed Non-Disclosure Agreements (NDAs). We maintain strict controls over who can view sensitive financial information and business valuations."
        },
        {
          id: "7",
          title: "7. Data Retention",
          text: "We retain your personal information for as long as your account remains active or as needed to provide you services. We may also retain certain information to comply with legal obligations, resolve disputes, enforce our agreements, and for legitimate business purposes. Upon account closure, your data may be retained in our archives for the period required by applicable laws."
        },
        {
          id: "8",
          title: "8. Your Rights and Choices",
          text: "Depending on your jurisdiction, you may have rights regarding your personal information, including: the right to access and receive a copy of your data; the right to correct inaccurate information; the right to request deletion of your data (subject to legal retention requirements); the right to restrict or object to certain processing; the right to data portability. To exercise these rights, please contact us at contact@acquizgroup.com."
        },
        {
          id: "9",
          title: "9. Cookies and Tracking Technologies",
          text: "We use cookies and similar tracking technologies to enhance your experience on our platform. These technologies help us understand how you interact with our services, remember your preferences, and provide personalized content. You can manage your cookie preferences through your browser settings, though disabling cookies may affect certain platform functionalities."
        },
        {
          id: "10",
          title: "10. Third-Party Links",
          text: "Our platform may contain links to third-party websites or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policies of any third-party sites you visit."
        },
        {
          id: "11",
          title: "11. International Data Transfers",
          text: "Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our platform, you consent to the transfer of your information to the United States and other jurisdictions where we operate."
        },
        {
          id: "12",
          title: "12. Children's Privacy",
          text: "Our platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without verification of parental consent, we will take steps to remove that information from our servers."
        },
        {
          id: "13",
          title: "13. Changes to This Policy",
          text: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the 'Version' date above. You are advised to review this Privacy Policy periodically for any changes. Continued use of the platform after modifications constitutes acceptance of the updated policy."
        },
        {
          id: "14",
          title: "14. Contact Information",
          text: "If you have any questions about this Privacy Policy or our data practices, please contact us at: AcquizGroup LLC, Email: contact@acquizgroup.com. For data protection inquiries or to exercise your privacy rights, please include 'Privacy Request' in your email subject line."
        }
      ]
    },
    fr: {
      title: "POLITIQUE DE CONFIDENTIALITÉ",
      version: "VERSION : 2025-01-15 RÉV. 1.0",
      langBtn: "EN",
      backBtn: "Retour au formulaire",
      footerNote: "En utilisant AcquizGroup, vous reconnaissez avoir lu et compris cette Politique de Confidentialité.",
      sections: [
        {
          id: "1",
          title: "1. Introduction",
          text: "AcquizGroup (« nous », « notre » ou « nos ») s'engage à protéger la confidentialité et la sécurité de vos informations personnelles. Cette Politique de Confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous utilisez notre plateforme. En accédant ou en utilisant AcquizGroup, vous consentez aux pratiques décrites dans cette politique."
        },
        {
          id: "2",
          title: "2. Informations que nous collectons",
          text: "Nous collectons les informations personnelles que vous fournissez volontairement lors de votre inscription sur notre plateforme, y compris mais sans s'y limiter : nom, adresse e-mail, numéro de téléphone, adresse professionnelle, informations sur l'entreprise, données financières, qualifications professionnelles et documents de vérification d'identité. Nous collectons également automatiquement certaines informations techniques telles que les adresses IP, le type de navigateur, les informations sur l'appareil et les données d'utilisation via des cookies et des technologies similaires."
        },
        {
          id: "3",
          title: "3. Comment nous utilisons vos informations",
          text: "Nous utilisons vos informations personnelles pour : fournir et maintenir les services de notre plateforme ; traiter les transactions et faciliter la mise en relation commerciale ; vérifier votre identité et vos qualifications ; communiquer avec vous concernant votre compte, les mises à jour et les supports promotionnels ; améliorer nos services et développer de nouvelles fonctionnalités ; respecter les obligations légales et faire appliquer nos conditions d'utilisation ; et effectuer des vérifications des antécédents lorsque cela est requis pour certains rôles d'utilisateurs (Prêteurs, Avocats, CPA, Consultants)."
        },
        {
          id: "4",
          title: "4. Partage et divulgation des informations",
          text: "Nous pouvons partager vos informations avec : d'autres utilisateurs de la plateforme selon les besoins pour faciliter les transactions commerciales (sous réserve d'accords de confidentialité) ; des prestataires de services tiers qui aident à exploiter notre plateforme ; des services de vérification des antécédents et de certification ; des processeurs de paiement pour le traitement des transactions ; les autorités légales lorsque la loi l'exige ou pour protéger nos droits. Nous ne vendons pas vos informations personnelles à des tiers à des fins de marketing."
        },
        {
          id: "5",
          title: "5. Sécurité des données",
          text: "Nous mettons en œuvre des mesures de sécurité conformes aux normes de l'industrie pour protéger vos informations personnelles, y compris le cryptage, les serveurs sécurisés et les contrôles d'accès. Cependant, aucune méthode de transmission sur Internet ou de stockage électronique n'est sûre à 100 %. Bien que nous nous efforcions d'utiliser des moyens commercialement acceptables pour protéger vos données, nous ne pouvons garantir leur sécurité absolue. Vous êtes responsable du maintien de la confidentialité de vos identifiants de compte."
        },
        {
          id: "6",
          title: "6. Mémorandum d'Information Confidentielle (CIM)",
          text: "Les documents financiers et les informations commerciales soumis dans le cadre du Mémorandum d'Information Confidentielle sont traités avec le plus haut niveau de confidentialité. L'accès aux données du CIM est limité aux utilisateurs vérifiés qui ont signé des Accords de Non-Divulgation (NDA). Nous maintenons des contrôles stricts sur qui peut consulter les informations financières sensibles et les évaluations d'entreprise."
        },
        {
          id: "7",
          title: "7. Conservation des données",
          text: "Nous conservons vos informations personnelles aussi longtemps que votre compte reste actif ou selon les besoins pour vous fournir des services. Nous pouvons également conserver certaines informations pour nous conformer aux obligations légales, résoudre les litiges, faire appliquer nos accords et pour des objectifs commerciaux légitimes. Lors de la fermeture du compte, vos données peuvent être conservées dans nos archives pendant la période requise par les lois applicables."
        },
        {
          id: "8",
          title: "8. Vos droits et choix",
          text: "Selon votre juridiction, vous pouvez avoir des droits concernant vos informations personnelles, notamment : le droit d'accéder et de recevoir une copie de vos données ; le droit de corriger les informations inexactes ; le droit de demander la suppression de vos données (sous réserve des exigences légales de conservation) ; le droit de restreindre ou de vous opposer à certains traitements ; le droit à la portabilité des données. Pour exercer ces droits, veuillez nous contacter à contact@acquizgroup.com."
        },
        {
          id: "9",
          title: "9. Cookies et technologies de suivi",
          text: "Nous utilisons des cookies et des technologies de suivi similaires pour améliorer votre expérience sur notre plateforme. Ces technologies nous aident à comprendre comment vous interagissez avec nos services, à mémoriser vos préférences et à fournir un contenu personnalisé. Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur, bien que la désactivation des cookies puisse affecter certaines fonctionnalités de la plateforme."
        },
        {
          id: "10",
          title: "10. Liens vers des tiers",
          text: "Notre plateforme peut contenir des liens vers des sites Web ou des services tiers qui ne sont pas exploités par nous. Nous n'avons aucun contrôle sur et n'assumons aucune responsabilité pour le contenu, les politiques de confidentialité ou les pratiques de tout site ou service tiers. Nous vous encourageons à consulter les politiques de confidentialité de tout site tiers que vous visitez."
        },
        {
          id: "11",
          title: "11. Transferts internationaux de données",
          text: "Vos informations peuvent être transférées et conservées sur des serveurs situés en dehors de votre état, province, pays ou autre juridiction gouvernementale où les lois sur la protection des données peuvent différer. En utilisant notre plateforme, vous consentez au transfert de vos informations vers les États-Unis et d'autres juridictions où nous opérons."
        },
        {
          id: "12",
          title: "12. Confidentialité des enfants",
          text: "Notre plateforme n'est pas destinée aux personnes de moins de 18 ans. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants. Si nous apprenons que nous avons collecté des informations personnelles d'un enfant sans vérification du consentement parental, nous prendrons des mesures pour supprimer ces informations de nos serveurs."
        },
        {
          id: "13",
          title: "13. Modifications de cette politique",
          text: "Nous pouvons mettre à jour cette Politique de Confidentialité de temps à autre. Nous vous informerons de tout changement en publiant la nouvelle Politique de Confidentialité sur cette page et en mettant à jour la date de « Version » ci-dessus. Nous vous conseillons de consulter périodiquement cette Politique de Confidentialité pour tout changement. L'utilisation continue de la plateforme après des modifications constitue l'acceptation de la politique mise à jour."
        },
        {
          id: "14",
          title: "14. Coordonnées",
          text: "Si vous avez des questions concernant cette Politique de Confidentialité ou nos pratiques en matière de données, veuillez nous contacter à : AcquizGroup LLC, E-mail : contact@acquizgroup.com. Pour les demandes de protection des données ou pour exercer vos droits de confidentialité, veuillez inclure « Demande de confidentialité » dans l'objet de votre e-mail."
        }
      ]
    }
  };

  const t = content[lang];

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a2940] via-[#243b53] to-[#1D314D]">
      {/* Header */}
      <header className="bg-[#2C3E50] border-b border-[#C9A86A]/30 shadow-lg">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#C9A86A]" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-wide">{t.title}</h1>
              <p className="text-xs text-gray-400">{t.version}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
            className="flex items-center gap-2 bg-[#C9A86A]/10 border-[#C9A86A]/30 text-[#C9A86A] hover:bg-[#C9A86A]/20"
          >
            <Globe className="w-4 h-4" />
            {t.langBtn}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-[#2C3E50]/80 rounded-xl border border-[#C9A86A]/20 shadow-2xl p-8">
          {/* Sections */}
          <div className="space-y-6">
            {t.sections.map((section) => (
              <div
                key={section.id}
                className="bg-[#1D314D]/50 rounded-lg p-6 border border-white/5 hover:border-[#C9A86A]/30 transition-all"
              >
                <div className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-[#C9A86A] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">{section.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-sm">{section.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2C3E50] border-t border-[#C9A86A]/30 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">{t.footerNote}</p>
            <Button
              onClick={handleGoBack}
              className="bg-[#C9A86A] text-[#1D314D] hover:bg-[#D4B87A] font-semibold flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backBtn}
            </Button>
          </div>
          <div className="text-center mt-6 text-gray-500 text-xs">
            © {new Date().getFullYear()} AcquizGroup LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
